import { observer } from "mobx-react-lite";
import { ArrowUpDownIcon, DatabaseIcon } from "lucide-react";

import { MinMaxConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const minMaxTypes = ["min", "max"] as const;

const validationSchema = createBlockSchema<MinMaxConfiguration>()
  .requireName()
  .addRule(required("configuration.type", "Min/Max type is required"))
  .addRule(required("configuration.source", "Source is required"))
  .build();

const MinMaxBlockCore = observer(
  ({ sources = createEmptySources() }: Omit<BlockProps<MinMaxConfiguration>, "DefaultFields">) => {
    const block = useBlock<MinMaxConfiguration>();
    const isReadonly = useIsReadonly();

    const handleJsonPathChange = (path: string) => {
      block.setConfiguration({
        ...block.configuration,
        json_path: path,
        type: block.configuration?.type || "min",
        source: block.configuration?.source || "",
      });
    };

    const handleTypeChange = (type: "min" | "max") => {
      block.setConfiguration({
        ...block.configuration,
        json_path: block.configuration?.json_path,
        type,
        source: block.configuration?.source || "",
      });
    };

    return (
      <>
        <SectionGroup
          title="Min/Max Configuration"
          description="Configure how to find minimum or maximum values"
          icon={<ArrowUpDownIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <ValidatedField name="JSON Path" path="">
              <div className="flex flex-col gap-2">
                <Input
                  className="w-full"
                  value={block.configuration?.json_path || ""}
                  onChange={e => handleJsonPathChange(e.target.value)}
                  placeholder="$[*].value"
                  disabled={isReadonly}
                />
                <div className="text-xs text-gray-500">
                  Use $[*].fieldName to select a field from each object in an array. If blank,
                  compares entire elements.
                </div>
              </div>
            </ValidatedField>

            <ValidatedField name="Min/Max Type" path="configuration.type">
              <Select
                value={block.configuration?.type || undefined}
                onValueChange={handleTypeChange}
                disabled={isReadonly}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {minMaxTypes.map(typeOption => (
                    <SelectItem key={typeOption} value={typeOption}>
                      {typeOption}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ValidatedField>
          </div>
        </SectionGroup>

        <SectionGroup
          title="Data Source"
          description="Select the source to find min/max values from"
          icon={<DatabaseIcon className="h-4 w-4" />}
        >
          <SingleSourceSelector
            sources={sources}
            sourceKey="source"
            validationPath="configuration.source"
            name="Source"
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<MinMaxConfiguration>(MinMaxBlockCore, {
  validationSchema,
  defaultConfiguration: {
    type: "min",
    source: "",
  },
});
